import type { OnboardingAnswers } from "../screens/OnboardingFlow";

/**
 * Read the onboarding answers the user filled in during /login.
 * Stored under `bsure_onboarding_v1` by OnboardingFlow's reducer +
 * persist() helper. The chat surface uses this to address the user
 * by name, tailor starter prompts to their persona, etc.
 *
 * Returns null when:
 *   - SSR (window undefined)
 *   - the user hasn't completed onboarding yet
 *   - the stored blob is corrupt
 *
 * Mirrors the migration shape readStored() uses inside OnboardingFlow
 * so a v6.1 stored answer with `language: "hinglish"` doesn't crash
 * the chat — we just drop the obsolete field and return the rest.
 */
export function readStoredAnswers(): OnboardingAnswers | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem("bsure_onboarding_v1");
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<OnboardingAnswers>;
    // Light shape check — name is the only field we *use* in the chat
    // empty state, so we don't bother validating the whole tree.
    if (typeof parsed !== "object" || parsed === null) return null;
    return parsed as OnboardingAnswers;
  } catch {
    return null;
  }
}
